import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, InputNumber, Switch, message, Space, Tag, Popconfirm, Typography, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CodeOutlined } from '@ant-design/icons';
import sslService from '../../services/sslService';
import I18nFieldTabs, { parseI18n, cleanI18n } from '../../components/I18nFieldTabs';

const { Option } = Select;
const { Title } = Typography;

const CodeSigningProducts = () => {
  const [products, setProducts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProviders();
    fetchProducts();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await sslService.getProviders(true);
      if (response.success) {
        // Filter providers that support code signing
        const codeSigningProviders = response.data.filter(p => p.supports_code_signing);
        setProviders(codeSigningProviders);
      }
    } catch (error) {
      message.error('Failed to fetch providers');
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await sslService.getProducts(true);
      if (response.success) {
        // Filter code signing products
        const codeSigningProducts = (response.data || []).filter(p =>
          p.category === 'code_signing' ||
          p.type?.toLowerCase().includes('code signing') ||
          p.type?.toLowerCase().includes('codesigning')
        );
        setProducts(codeSigningProducts);
      }
    } catch (error) {
      message.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({ category: 'code_signing' });
    setModalVisible(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({ ...product, i18n: parseI18n(product.i18n) });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await sslService.deleteProduct(id);
      message.success('Product deleted');
      fetchProducts();
    } catch (error) {
      message.error('Failed to delete');
    }
  };

  const handleSubmit = async (formValues) => {
    const values = { ...formValues, i18n: cleanI18n(formValues.i18n) };
    try {
      // Ensure category is set
      values.category = 'code_signing';
      values.requires_csr = false;
      values.requires_domain = false;

      if (editingProduct) {
        await sslService.updateProduct(editingProduct.id, values);
        message.success('Product updated');
      } else {
        await sslService.createProduct(values);
        message.success('Product created');
      }
      setModalVisible(false);
      fetchProducts();
    } catch (error) {
      message.error('Failed to save');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.name}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{record.product_code}</div>
        </div>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => <Tag color="blue">{provider}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const color = type?.includes('EV') ? 'gold' : 'blue';
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: 'Validity',
      dataIndex: 'validity_period_days',
      key: 'validity_period_days',
      render: (days) => days ? `${Math.round(days / 365)} year(s)` : '-',
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => (
        <div>
          <div>Cost: ${record.cost_price || '-'}</div>
          <div>Sale: ${record.sale_price || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm title="Delete this product?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <CodeOutlined />
          <span>代码签名产品</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加产品
        </Button>
      }
    >
      <Alert
        message="代码签名证书管理"
        description="管理代码签名产品，包括标准代码签名 (OV) 和 EV 代码签名。这些产品使用 SimplySign 云签名平台，不需要 CSR 和域名验证。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {providers.length === 0 && (
        <Alert
          message="无可用的代码签名提供商"
          description="请先在 Provider 配置中启用支持代码签名的提供商（如 Certum）。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
      />

      <Modal
        title={editingProduct ? '编辑代码签名产品' : '添加代码签名产品'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
            <Select placeholder="选择提供商">
              {providers.map(p => (
                <Option key={p.provider} value={p.provider}>{p.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="product_code" label="Product Code" rules={[{ required: true }]}>
            <Input placeholder="e.g., 831, 316" />
          </Form.Item>
          <I18nFieldTabs
            fields={[{ key: 'name', label: 'Product Name' }, { key: 'description', label: 'Description', type: 'textarea' }]}
            base={<>
              <Form.Item name="name" label="Product Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Standard Code Signing (1 Year)" />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
            </>}
          />
          <Form.Item name="brand" label="Brand">
            <Input placeholder="e.g., Certum" />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select>
              <Option value="Code Signing">标准代码签名 (OV)</Option>
              <Option value="EV Code Signing">EV 代码签名</Option>
            </Select>
          </Form.Item>
          <Form.Item name="validity_period_days" label="Validity (Days)" rules={[{ required: true }]}>
            <Select>
              <Option value={365}>1 Year (365 days)</Option>
              <Option value={730}>2 Years (730 days)</Option>
              <Option value={1095}>3 Years (1095 days)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="cost_price" label="Cost Price (USD)">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sale_price" label="Sale Price (USD)">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Save</Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CodeSigningProducts;
